// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import React, { RefObject, useEffect, useRef, useState } from "react";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import AccountCreationFormFields from "@store/Library/Components/AccountCreations/AccountCreationFormFields";
import submitAccountCreationForm from "@store/Framework/Store/Store/Components/AccountCreationForm/Handlers/SubmitAccountCreationForm";
import clearForm from "@store/Framework/Store/Store/Components/AccountCreationForm/Handlers/ClearForm";

const enum fields {
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const { userName } = state.context.session;
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    return {
        fieldValues: state.store.components.accountCreationForm.fieldValues,
        isSignedIn: userName && !currentUserIsGuest,
    };
};
const mapDispatchToProps = {
    submitAccountCreationForm,
    clearForm,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountCreationFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
    successBox?: InfosBoxProps;
    legalNoticeBox?: InfosBoxProps;
}

export const accountCreationFormStyles: AccountCreationFormStyles = {
    container: {
        css: css`
            form {
                width: 100%;
            }
        `,
    },
    formGridItem: { width: 12 },
    successModal: { sizeVariant: "small" },
    successBox: {
        type: "success",
    },
    legalNoticeBox: {
        type: "info",
        extendedStyles: {
            content: {
                css: css`
                    width: 100%;
                `,
            },
        },
    },
};

const styles = accountCreationFormStyles;

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const AccountCreationFormContext = React.createContext<ContextType>({
    validators: {},
});

const AccountCreationForm = ({
    id,
    fields,
    validateReCaptcha,
    submitAccountCreationForm,
    clearForm,
    isSignedIn,
}: Props) => {
    const { submitButtonText, successMessage } = fields;
    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();

    const trackFormStart = ga.use.trackFormStart({ form_name: "account_creation_form" });
    const trackFormSubmit = ga.use.trackFormSubmit({ form_name: "account_creation_form" });
    const canTrackFormStartRef = useRef(true);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        const handleFormFocus = () => {
            if (formRef.current?.contains(document.activeElement) && canTrackFormStartRef.current) {
                trackFormStart();
                canTrackFormStartRef.current = false;
            }
        };

        formRef.current?.addEventListener("focus", handleFormFocus, true);

        return () => {
            formRef.current?.removeEventListener("focus", handleFormFocus);
        };
    }, []);

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const widgetValidators: Validator[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v());
        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        submitAccountCreationForm({
            onSuccess: () => {
                setIsModalOpen(true);
                setIsSubmitting(false);
                trackFormSubmit();
                canTrackFormStartRef.current = true;
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC

                this.onSuccess?.();
            },
        });
    };

    const modalCloseHandler = () => {
        clearForm();
        setIsModalOpen(false);
        window.location.reload();
    };

    if (isSignedIn) {
        return <>{translate("Signed in users cannot create a new account. Please log off first.")}</>;
    }

    return (
        <FormWrapper noFocusNoScroll>
            <GridContainer {...styles.container}>
                <GridItem {...styles.formGridItem}>
                    <AccountCreationFormContext.Provider value={{ validators }}>
                        <form id="accountCreationForm" onSubmit={handleFormSubmit} noValidate ref={formRef}>
                            <Zone contentId={id} zoneName="Content" />
                            <AccountCreationFormFields />
                            <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                            <FormActions>
                                <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                                    {submitButtonText}
                                </Button>
                            </FormActions>
                            <br />
                            <Modal
                                headline={translate("Message Sent")}
                                contentLabel={successMessage}
                                {...styles.successModal}
                                isOpen={isModalOpen}
                                handleClose={modalCloseHandler}
                            >
                                <InfosBox {...styles.successBox}>{successMessage}</InfosBox>
                                <FormActions>
                                    <Button {...styles.continueButton} onClick={modalCloseHandler}>
                                        {translate("Continue")}
                                    </Button>
                                </FormActions>
                            </Modal>
                        </form>
                    </AccountCreationFormContext.Provider>
                </GridItem>
            </GridContainer>
        </FormWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountCreationForm),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
        ],
    },
};

export default widgetModule;
